import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";

// Komunikaty (notify) i okno potwierdzenia (confirm) zamiast window.alert / window.confirm.
// Systemowe okienka blokowały całą kartę, wyglądały w każdej przeglądarce inaczej
// i miały przyciski w języku systemu. Wspólne dla panelu (A24, etykiety z i18n panelu -
// adminPane/context/DialogContext.tsx) i konfiguratora (K1, etykiety po niemiecku - App.tsx).
// Wygląd: panel - klasy pod .admin-panel (admin-theme.css, AdminPanel.css),
// konfigurator - pod .lm-dialogs (dialogs.css).

export type NoticeType = "success" | "error" | "info";

interface Notice {
  id: number;
  type: NoticeType;
  message: string;
}

export interface ConfirmOptions {
  message: string;
  title?: string;
  confirmLabel?: string;
  // Usuwanie / nadpisywanie: czerwony przycisk, a domyślnie zaznaczone jest "Abbrechen"
  danger?: boolean;
}

export interface DialogLabels {
  confirmTitle: string;
  ok: string;
  cancel: string;
  close: string;
}

interface DialogApi {
  notify: (message: string, type?: NoticeType) => void;
  confirm: (options: ConfirmOptions | string) => Promise<boolean>;
}

const DialogContext = createContext<DialogApi | undefined>(undefined);

// Błędy wiszą dłużej - trzeba zdążyć je przeczytać
const NOTICE_TIMEOUT: Record<NoticeType, number> = { success: 4000, info: 5000, error: 9000 };
const MAX_NOTICES = 4;

const NOTICE_ICONS: Record<NoticeType, React.ReactNode> = {
  success: <CheckCircle2 size={18} aria-hidden="true" />,
  error: <AlertTriangle size={18} aria-hidden="true" />,
  info: <Info size={18} aria-hidden="true" />,
};

interface ConfirmDialogProps {
  options: ConfirmOptions;
  labels: DialogLabels;
  onClose: (confirmed: boolean) => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ options, labels, onClose }) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Przy usuwaniu Enter nie może skasować danych przez przypadek
    (options.danger ? cancelRef : confirmRef).current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [options, onClose]);

  return (
    <div
      className="dialog-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose(false);
      }}
    >
      <div
        className="dialog-card confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-message"
      >
        <h3 id="confirm-dialog-title">{options.title ?? labels.confirmTitle}</h3>
        <p id="confirm-dialog-message" className="confirm-dialog__message">
          {options.message}
        </p>
        <div className="dialog-actions">
          <button type="button" className="dialog-cancel" ref={cancelRef} onClick={() => onClose(false)}>
            {labels.cancel}
          </button>
          <button
            type="button"
            className={options.danger ? "dialog-danger" : undefined}
            ref={confirmRef}
            onClick={() => onClose(true)}
          >
            {options.confirmLabel ?? labels.ok}
          </button>
        </div>
      </div>
    </div>
  );
};

interface DialogProviderProps {
  labels: DialogLabels;
  // Opcjonalny kontener na komunikaty i okno - konfigurator ma w nim swoje style (.lm-dialogs)
  className?: string;
  children: ReactNode;
}

export const DialogProvider: React.FC<DialogProviderProps> = ({ labels, className, children }) => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [pending, setPending] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<((confirmed: boolean) => void) | null>(null);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setNotices((current) => current.filter((notice) => notice.id !== id));
  }, []);

  const notify = useCallback(
    (message: string, type: NoticeType = "info") => {
      const id = nextId.current++;
      setNotices((current) => [...current.slice(-(MAX_NOTICES - 1)), { id, type, message }]);
      window.setTimeout(() => dismiss(id), NOTICE_TIMEOUT[type]);
    },
    [dismiss]
  );

  const confirm = useCallback((options: ConfirmOptions | string) => {
    // Nowe pytanie zamyka poprzednie jako "nie" - obietnica nie może zawisnąć
    resolveRef.current?.(false);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setPending(typeof options === "string" ? { message: options } : options);
    });
  }, []);

  const closeConfirm = useCallback((confirmed: boolean) => {
    resolveRef.current?.(confirmed);
    resolveRef.current = null;
    setPending(null);
  }, []);

  const overlays = (
    <>
      <div className="dialog-toasts" aria-live="polite">
        {notices.map((notice) => (
          <div
            key={notice.id}
            className={`dialog-toast dialog-toast--${notice.type}`}
            role={notice.type === "error" ? "alert" : "status"}
          >
            <span className="dialog-toast__icon">{NOTICE_ICONS[notice.type]}</span>
            <span className="dialog-toast__message">{notice.message}</span>
            <button
              type="button"
              className="dialog-toast__close"
              onClick={() => dismiss(notice.id)}
              aria-label={labels.close}
              title={labels.close}
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
      {pending && <ConfirmDialog options={pending} labels={labels} onClose={closeConfirm} />}
    </>
  );

  return (
    <DialogContext.Provider value={{ notify, confirm }}>
      {children}
      {className ? <div className={className}>{overlays}</div> : overlays}
    </DialogContext.Provider>
  );
};

export const useDialogs = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("useDialogs must be used within a DialogProvider");
  }
  return context;
};
